import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand from "@insite/content-library/Components/ProductBrand";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import Block from "@store/Components/Common/Block";
import { storeTearsheetProductDetailsViewStyles as tearsheetStyles } from "@store/Components/Store/StoreTearsheetProductDetailsView";
import { useHasAttributes, useHasDescription } from "@store/Hooks/Product";
import ProductPrimaryImage from "@store/Overrides/Components/ProductPrimaryImage";
import { primaryImageStyles } from "@store/Overrides/Widgets/ProductDetails/ProductDetailsPrimaryImage";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import { StoreDetailsViewTearsheetProps } from "@store/Widgets/ProductDetailsTearsheet/TearsheetProductDetailsView";
import parse from "html-react-parser";
import React, { FC, useContext } from "react";

const PageTitle = getWidgetComponent("ProductDetails/ProductDetailsPageTitle");
const PartNumber = getWidgetComponent("ProductDetails/ProductDetailsPartNumber");
const Description = getWidgetComponent("ProductDetails/ProductDetailsDescription");
const Attributes = getWidgetComponent("ProductDetails/ProductDetailsAttributes");

const StoreTearsheetProductDetailsView: FC<StoreDetailsViewTearsheetProps> = props => {
    const { product } = useContext(ProductContext);
    const hasDescription = useHasDescription();
    const hasAttributes = useHasAttributes();

    const image = {
        id: product.id,
        imageAltText: product.imageAltText,
        imageType: "Static",
        largeImagePath: product.largeImagePath,
        mediumImagePath: product.mediumImagePath,
        smallImagePath: product.smallImagePath,
    } as ImageModel;

    return (
        <Block {...tearsheetStyles.detail?.block}>
            <GridContainer {...tearsheetStyles.detail?.container}>
                <GridItem {...tearsheetStyles.detail?.contentGridItem}>
                    <GridContainer {...tearsheetStyles.detail?.content?.container}>
                        {product.brand ? (
                            <GridItem {...tearsheetStyles.detail?.content?.gridItem}>
                                <ProductBrand brand={product.brand} />
                            </GridItem>
                        ) : null}
                        <GridItem {...tearsheetStyles.detail?.content?.gridItem}>
                            <PageTitle />
                        </GridItem>
                        {product.productNumber ? (
                            <GridItem {...tearsheetStyles.detail?.content?.gridItem}>
                                <PartNumber />
                            </GridItem>
                        ) : null}
                        {hasDescription ? (
                            <>
                                <GridItem {...tearsheetStyles.detail?.content?.gridItem}>
                                    <Typography as="h2">{translate("Description")}</Typography>
                                    <Description />
                                </GridItem>
                            </>
                        ) : null}
                    </GridContainer>
                </GridItem>
                <GridItem {...tearsheetStyles.detail?.imageGridItem}>
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        useLargeImage={true}
                        extendedStyles={primaryImageStyles.primaryImage}
                    />
                </GridItem>
            </GridContainer>
            {hasDescription || hasAttributes || product.specifications?.length ? (
                <GridContainer {...tearsheetStyles.specifications?.specificationsContainer}>
                    {hasAttributes ? (
                        <>
                            <GridItem {...tearsheetStyles.specifications?.titleGridItem}>
                                <Typography as="h2">{translate("Specifications")}</Typography>
                            </GridItem>
                            <GridItem {...tearsheetStyles.specifications?.contentGridItem}>
                                <Attributes />
                            </GridItem>
                        </>
                    ) : null}
                    {product.specifications?.map(tab => (
                        <>
                            <GridItem {...tearsheetStyles.detail?.imageGridItem}>
                                <Typography as="h2">{tab.nameDisplay}</Typography>
                            </GridItem>
                            <GridItem {...tearsheetStyles.detail?.contentGridItem}>
                                {parse(tab.htmlContent, parserOptions)}
                            </GridItem>
                        </>
                    ))}
                </GridContainer>
            ) : null}
            <GridContainer {...tearsheetStyles?.bottomContent?.bottomContentContainer}>
                <GridItem {...tearsheetStyles?.bottomContent?.bottomContentGridItemFull}>
                    {parse(props.fullWidthContent, parserOptions)}
                </GridItem>
                <GridItem {...tearsheetStyles?.bottomContent?.bottomContentGridItemLeft}>
                    {parse(props.bottomLeftcontent, parserOptions)}
                </GridItem>
                <GridItem {...tearsheetStyles?.bottomContent?.bottomContentGridItemRight}>
                    {parse(props.bottomRightContent, parserOptions)}
                </GridItem>
            </GridContainer>
        </Block>
    );
};

export default StoreTearsheetProductDetailsView;
